import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const RequestCtx = createContext(null);

/**
 * Inbox for admin permission requests addressed to the signed-in user.
 *
 * The user alone decides whether an admin gets access, so this context is
 * mounted for every user (not just admins) and polls while the tab is visible.
 * `count` drives the badge in the header; `respond` performs the approval.
 */
export function PermissionRequestProvider({ children }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/permission-requests");
      if (mounted.current) setRequests(Array.isArray(data) ? data : []);
    } catch {
      // Silent: an unreachable inbox should not disrupt the app.
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 60000);
    return () => {
      mounted.current = false;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [refresh]);

  const pending = useMemo(
    () => requests.filter((r) => r.status === "pending"),
    [requests],
  );

  const respond = useCallback(
    async (requestId, decision, note = "") => {
      try {
        const { data } = await api.post(
          `/auth/permission-requests/${requestId}/respond`,
          { decision, note },
        );
        await refresh();
        if (decision === "approve") {
          const granted = Object.keys(data?.granted || {});
          toast.success(
            granted.length
              ? `Approved. Administrator can now ${granted
                  .map((g) => (g.endsWith("modify") ? "edit" : "view"))
                  .join(" and ")} your data.`
              : "Request approved.",
          );
        } else {
          toast.success("Request rejected. The administrator was not given access.");
        }
        return data;
      } catch (e) {
        toast.error(formatApiError(e));
        throw e;
      }
    },
    [refresh],
  );

  const value = useMemo(
    () => ({ requests, pending, pendingCount: pending.length, loading, refresh, respond }),
    [requests, pending, loading, refresh, respond],
  );

  return <RequestCtx.Provider value={value}>{children}</RequestCtx.Provider>;
}

export const usePermissionRequests = () => {
  const ctx = useContext(RequestCtx);
  if (!ctx)
    throw new Error("usePermissionRequests must be used inside <PermissionRequestProvider>");
  return ctx;
};